import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { useAuth } from '../../Components/AuthContext';

const HomePage = () => {
  const { tenantDomain } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userObj, setUserObj] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAllele, setSelectedAllele] = useState('');
  const [availableAlleles, setAvailableAlleles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingAlleles, setIsLoadingAlleles] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    try {
      if (!user) {
        return; // Skip validation if user isn't loaded yet
      }
      const userObj = typeof user === 'string' ? JSON.parse(user) : user;
      setUserObj(userObj)
      // Validate that the JWT tenant matches the URL tenant
      if (userObj.tenantDomain !== tenantDomain) {
        navigate(`/${tenantDomain}/login`, { 
          state: { error: 'Access denied. Please login to the correct tenant.' }
        });
        return;
      }
    } catch (error) {
      console.error('Error parsing JWT:', error);
      navigate(`/${tenantDomain}/login`);
    }
  }, [tenantDomain, navigate, user]);

  useEffect(() => {
    const fetchAlleles = async () => {
      if (!userObj) return;
      
      setIsLoadingAlleles(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/appm/alleles`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const alleles = await response.json();
        setAvailableAlleles(alleles.alleles || alleles);
      } catch (err) {
        console.error('Error fetching alleles:', err);
        setError('Failed to load available alleles');
      } finally {
        setIsLoadingAlleles(false);
      }
    };

    fetchAlleles();
  }, [userObj, tenantDomain]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.toLowerCase().endsWith('.csv') || 
                 file.type === 'text/csv' || 
                 file.type === 'application/csv' || 
                 file.type === 'text/plain')) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!selectedAllele) {
      setError('Please select an allele');
      return;
    }

    setIsUploading(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('allele', selectedAllele);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/appm/predict`, {
        method: 'POST',
        body: formData,
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle Excel file download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'peptide_predictions.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      setResults({ message: 'Prediction complete! Excel file downloaded successfully.' });
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (!userObj) {
    return null; // or a loading spinner
  }

  return (
    <div className="d-flex">
        <MainLayout>
      {/* <div className="bg-light" style={{ minWidth: '250px', minHeight: '100vh' }}> */}
        {/* <div className="p-3"> */}
          {/* <h5 className="mb-3">Navigation</h5> */}
          {/* <Nav className="flex-column"> */}
            {/* <Nav.Link className="active">Dashboard</Nav.Link> */}
            {/* <Nav.Link>Profile</Nav.Link> */}
            {/* <Nav.Link>Settings</Nav.Link> */}
            {/* <Nav.Link onClick={handleLogout}>Logout</Nav.Link> */}
          {/* </Nav> */}
        {/* </div> */}
      {/* </div> */}

      {/* Main content */}
      <Container fluid className="p-4">
        <Row className="mb-4">
          <Col>
            <h2>APPM Peptide Prediction</h2>
            <p className="text-muted">Upload a CSV file with peptide sequences and HLA alleles for binding prediction</p>
            <div className="bg-light p-3 rounded border-start border-primary border-4 mb-3">
              <h6 className="mb-2">
                <i className="fas fa-graduation-cap me-2 text-primary"></i>
                Research Citation
              </h6>
              <p className="mb-2 small">
                This tool implements the APPM (Antigen Presentation Prediction Model) neural network from:
              </p>
              <p className="mb-1 small">
                <strong>He H, Xu H, Zhang Y, et al.</strong> APPM: Antigen presentation prediction model based on convolutional neural network. 
                <em>Comput Struct Biotechnol J</em>. 2021;19:2647-2656. 
                <a 
                  href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8186784/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-decoration-none ms-1"
                >
                  <i className="fas fa-external-link-alt"></i> View Paper
                </a>
              </p>
              <p className="mb-0 small">
                <strong>Source Code:</strong> 
                <a 
                  href="https://github.com/haoqing12/APPM" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-decoration-none ms-1"
                >
                  <i className="fab fa-github"></i> GitHub Repository
                </a>
              </p>
            </div>
          </Col>
        </Row>

        {error && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <Col>
            <Card className="h-100">
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">Upload CSV File for Peptide Prediction</h4>
              </Card.Header>
              <Card.Body className="p-5">
                <Row className="align-items-center" style={{ minHeight: '300px' }}>
                  <Col md={8}>
                    <div className="mb-4">
                      <label htmlFor="alleleSelect" className="form-label h5">Select HLA Allele:</label>
                      <Form.Select
                        id="alleleSelect"
                        value={selectedAllele}
                        onChange={(e) => setSelectedAllele(e.target.value)}
                        disabled={isUploading || isLoadingAlleles}
                        size="lg"
                      >
                        <option value="">
                          {isLoadingAlleles ? 'Loading alleles...' : 'Choose an allele...'}
                        </option>
                        {availableAlleles.map((allele, index) => (
                          <option key={index} value={allele}>
                            {allele}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="csvFile" className="form-label h5">Select your CSV file:</label>
                      <input
                        id="csvFile"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="form-control form-control-lg"
                        disabled={isUploading}
                      />
                    </div>
                    <div className="mb-4 p-3 bg-light rounded">
                      <h6 className="mb-2">File Format Requirements:</h6>
                      <ul className="mb-2">
                        <li>CSV file with two columns: <strong>"Peptide"</strong> and <strong>"HLA"</strong></li>
                        <li>Each row should contain a peptide sequence and corresponding HLA allele</li>
                        <li>Example format:</li>
                      </ul>
                      <div className="bg-white p-2 rounded border">
                        <code>
                          Peptide,HLA<br/>
                          EGWLHKRGK,A*01:01<br/>
                          GWLHKRGKY,A*01:01
                        </code>
                      </div>
                    </div>
                    {selectedFile && (
                      <div className="mb-4 p-3 bg-success bg-opacity-10 border border-success rounded">
                        <h6 className="text-success mb-1">File Selected:</h6>
                        <p className="mb-0"><strong>{selectedFile.name}</strong> ({Math.round(selectedFile.size / 1024)} KB)</p>
                      </div>
                    )}
                  </Col>
                  <Col md={4} className="text-center">
                    <Button
                      onClick={handleUpload}
                      disabled={!selectedFile || !selectedAllele || isUploading}
                      variant="primary"
                      size="lg"
                      className="px-5 py-3"
                    >
                      {isUploading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload me-2"></i>
                          Upload and Predict
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {results && (
          <Row>
            <Col>
              <Alert variant="success" className="d-flex align-items-center">
                <i className="fas fa-check-circle me-2"></i>
                {results.message}
              </Alert>
            </Col>
          </Row>
        )}
      </Container>
      </MainLayout>
    </div>
  );
};

export default HomePage;